import { useState } from 'react';
import { useLazyQuery } from 'apollo-client-ppc';
import { abgGraphqlClient as graphqlClient, ProxyGraphClients } from '@atlassian/temp-client-utils';
import { fetchInvoicesForTransactionAccount } from '@css-xp/billing-v2';
import {
  FetchInvoicesForTransactionAccountV2Query,
  FetchInvoicesForTransactionAccountV2QueryVariables,
  FetchOrdersByOrderLineIdQuery,
  FetchOrdersByOrderLineIdQueryVariables,
  InvoiceLineItems,
  Invoice,
  Maybe,
  InvoicesFilterOptions,
  FetchCreditNoteWithInvoicesQuery,
  FetchCreditNoteWithInvoicesQueryVariables,
  SortType,
} from '@css-xp/graphtypes/src/generated-types-new';
import { generatedTypesSxp } from '@css-xp/graphtypes';
import { useNotification, errorNotificationBuilder, NotificationTypes } from '@css-xp/base';
import { maxThresholdForResults } from '@css-xp/ppc-constants';
import { fetchCreditNoteWithInvoices } from '@css-xp/ppc-invoice-table/src/queries/fetch-invoices-with-refunds';
import { downloadAsCSV } from '@css-xp/ppc-utils';
import { NOTIFICATION_DISMISS_TIME } from '../constants/invoices';
import { fetchOrdersByOrderLineId } from '../queries/fetch-orders-by-order-line-id';
import { fetchQuotesByOrderLineId } from '../queries/fetch-quotes-by-order-line-id';

export const useInvoicesListCSV = (
  txaId: string,
  filterOptions?: InvoicesFilterOptions,
  selectedTab?: string,
  sortOptions?: SortType
) => {
  const [isLoading, setIsLoading] = useState(false);
  const [invoicesListLoading, setInvoicesListLoading] = useState(false);
  const [refundInvoicesListLoading, setRefundInvoicesListLoading] = useState(false);
  const { sendErrorNotification, setNotificationSent } = useNotification();
  const [loadInvoicesList] = useLazyQuery<
    FetchInvoicesForTransactionAccountV2Query,
    FetchInvoicesForTransactionAccountV2QueryVariables
  >(fetchInvoicesForTransactionAccount, {
    variables: {
      id: txaId,
      searchOptions: { first: maxThresholdForResults, offset: 0, sort: sortOptions },
      includeItems: true,
      filterOptions,
    },
    notifyOnNetworkStatusChange: true,
  });

  const [loadRefundInvoicesList] = useLazyQuery<
    FetchCreditNoteWithInvoicesQuery,
    FetchCreditNoteWithInvoicesQueryVariables
  >(fetchCreditNoteWithInvoices, {
    variables: {
      id: txaId,
      searchOptions: { first: 9999, offset: 0 },
      filterOptions: filterOptions,
    },
    notifyOnNetworkStatusChange: true,
  });

  const csvHeaders = ['Invoice number', 'Status', 'Due', 'Issued', 'Amount', 'Company', 'Payment method', 'PO number'];

  const getOrderNumberValue = (orderNumberData: any) => {
    let orderNumber = '-';

    const orders = orderNumberData?.transactionAccount?.orders;
    const ordersCount = orders?.totalCount;

    if (ordersCount) {
      orderNumber = orders?.nodes
        ?.map((order: any) => {
          return order?.orderNumber;
        })
        .join(', ');
    }

    return orderNumber;
  };

  const getOrderNumberString = async (lineItems: Maybe<Maybe<InvoiceLineItems>[]> | undefined) => {
    if (lineItems?.length) {
      const orderLineIds = lineItems?.map((lineItem: InvoiceLineItems | null) => lineItem?.orderLineId);

      // Note: Using graphqlClient().query so that correct order numbers get associated with the invoice
      // which is not the case when useLazyQuery is used. This is same logic as used in `useOrderNumberData` hook.
      const { data } = await graphqlClient().query<
        FetchOrdersByOrderLineIdQuery,
        FetchOrdersByOrderLineIdQueryVariables
      >({
        query: fetchOrdersByOrderLineId,
        variables: {
          transactionAccountId: txaId,
          filterOptions: {
            or: orderLineIds?.map((id?: Maybe<string>) => {
              return {
                key: 'orderLineItemsId',
                value: id,
              };
            }),
          },
        },
      });

      return getOrderNumberValue(data);
    }

    return '-';
  };

  const getQuoteNumberValue = (data: any) => {
    let quoteNumber = '-';

    const quotes = data?.quotes;
    const totalCount = quotes?.totalCount;

    if (totalCount) {
      quoteNumber = quotes?.nodes
        ?.map((quote: any) => {
          return quote?.quoteNumber;
        })
        .join(', ');
    }

    return quoteNumber;
  };

  const getQuoteNumberString = async (lineItems: Maybe<Maybe<InvoiceLineItems>[]> | undefined) => {
    if (lineItems?.length) {
      const orderLineIds = lineItems?.map((lineItem: InvoiceLineItems | null) => lineItem?.orderLineId ?? '');
      const filterValues = orderLineIds ? orderLineIds : [''];

      // Note: Using graphqlClient().query so that correct quote number gets associated with the invoice
      // which is not the case when useLazyQuery is used. This is same logic as used in `useQuoteNumberData` hook.
      const { data } = await graphqlClient(ProxyGraphClients.SXP).query<
        generatedTypesSxp.FetchQuotesByOrderLineIdQuery,
        generatedTypesSxp.FetchQuotesByOrderLineIdQueryVariables
      >({
        query: fetchQuotesByOrderLineId,
        variables: {
          filterOptions: {
            and: [
              {
                key: 'orderLineItemIdIsIn',
                values: filterValues,
              },
              { key: 'transactionAccountId', value: txaId },
            ],
          },
        },
      });

      return getQuoteNumberValue(data);
    }

    return '-';
  };

  const downloadInvoicesListCSV = async () => {
    try {
      let invoicesList;

      if (selectedTab === 'refunded') {
        setRefundInvoicesListLoading(true);
        const { data, error } = await loadRefundInvoicesList();
        setRefundInvoicesListLoading(false);
        if (error) throw error;
        invoicesList = data?.transactionAccount?.refundedInvoices?.nodes;
      } else {
        setInvoicesListLoading(true);
        const { data, error } = await loadInvoicesList();
        setInvoicesListLoading(false);
        if (error) throw error;
        invoicesList = data?.transactionAccount?.invoices?.nodes;
      }

      if (invoicesList) {
        const invoiceNumberToOrderNumberMap: Record<string, string> = {};
        const invoiceNumberToQuoteNumberMap: Record<string, string> = {};

        const promisesArray = invoicesList.map(async (node) => {
          const lineItems = (node as Invoice)?.lineItems;
          const orderNumber = await getOrderNumberString(lineItems);
          const quoteNumber = await getQuoteNumberString(lineItems);

          if (node?.invoiceNumber) {
            const invoiceNumber = node.invoiceNumber;

            invoiceNumberToOrderNumberMap[invoiceNumber] = orderNumber;
            invoiceNumberToQuoteNumberMap[invoiceNumber] = quoteNumber;
          }
        });

        if (promisesArray) {
          setIsLoading(true);
          await Promise.all(promisesArray);
          setIsLoading(false);
          // Add Order numbers header
          csvHeaders.push('Order numbers');
          // Add Quote number header
          csvHeaders.push('Quote number');
        }

        csvHeaders.splice(0, 0, 'Invoice Id');

        downloadAsCSV({
          fileName: 'invoices-list',
          header: csvHeaders,
          data: invoicesList.map((node) => {
            const values = [
              node?.invoiceNumber ?? '-',
              node?.status ?? '-',
              node?.dueDate ?? '-',
              node?.createdDate ?? '-',
              node?.grandTotal ? `${node?.paymentCurrency} ${node?.grandTotal}` : '-',
              node?.shipTo?.fullName ?? '-',
              node?.paymentMethod ?? '-',
              node?.purchaseOrderNumber ?? '-',
            ];

            values.splice(0, 0, node?.id ?? '-');

            const invoiceNumber = node?.invoiceNumber;

            // Add Order numbers value
            values.push((invoiceNumber && invoiceNumberToOrderNumberMap[invoiceNumber]) ?? '-');
            // Add Quote number value
            values.push((invoiceNumber && invoiceNumberToQuoteNumberMap[invoiceNumber]) ?? '-');

            return values;
          }),
        });
      }
    } catch (error) {
      setIsLoading(false);

      const errorNotificationOptions = errorNotificationBuilder()
        .withId('fetch-error-export-csv')
        .withDismissAfter(NOTIFICATION_DISMISS_TIME)
        .isVersion(NotificationTypes.FLAG);
      const options = errorNotificationOptions
        .withHeader('Unable to fetch details')
        .withMessage("We're having trouble getting all the details. Try again later.")
        .build();

      sendErrorNotification(options);
      setNotificationSent(true);
    }
  };

  return {
    downloadInvoicesListCSV,
    invoicesListLoading: invoicesListLoading || isLoading || refundInvoicesListLoading,
  };
};
